import React, { Suspense, useRef, useState, useMemo, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Billboard, Text, OrbitControls, Stars } from "@react-three/drei";
import { configureTextBuilder } from "troika-three-text";

// Generate glyph SDFs on the main thread. There are only a couple dozen short
// labels, so the cost is negligible, and it avoids spinning up a worker blob
// (which some strict CSP setups block and which delays first paint).
configureTextBuilder({ useWorker: false });

/**
 * Skills pulled from Harshil's resume, grouped by domain.
 * Colour palette stays in the site's green / teal accent family
 * with a couple of warm accents so categories stay readable.
 */
const GREEN = "#14e956";
const TEAL = "#2fe6c4";
const MINT = "#7bf5a8";
const AMBER = "#ffb454";
const SKY = "#66add3";

const SKILLS = [
  // Cloud & Infra
  { label: "AWS", color: GREEN, size: 1.1 },
  { label: "GCP", color: GREEN, size: 1.0 },
  { label: "Docker", color: GREEN, size: 1.0 },
  { label: "Bare-Metal", color: MINT, size: 0.8 },
  { label: "Proxmox", color: MINT, size: 0.8 },
  { label: "CI/CD", color: MINT, size: 0.85 },
  { label: "Linux", color: GREEN, size: 0.95 },
  // Backend
  { label: "Node.js", color: GREEN, size: 1.15 },
  { label: "REST APIs", color: MINT, size: 0.85 },
  { label: "JavaScript", color: MINT, size: 0.9 },
  { label: "Python", color: TEAL, size: 0.95 },
  { label: "System Design", color: TEAL, size: 0.85 },
  { label: "Microservices", color: MINT, size: 0.8 },
  // Data
  { label: "MongoDB", color: TEAL, size: 1.0 },
  { label: "MySQL", color: TEAL, size: 0.85 },
  { label: "ArangoDB", color: TEAL, size: 0.8 },
  { label: "SQL · NoSQL", color: TEAL, size: 0.8 },
  { label: "DB Performance", color: MINT, size: 0.8 },
  // Observability & Networking
  { label: "Prometheus", color: AMBER, size: 0.85 },
  { label: "Grafana", color: AMBER, size: 0.85 },
  { label: "Networking", color: SKY, size: 0.95 },
  { label: "Mail Servers", color: SKY, size: 0.8 },
  // Security & Domain
  { label: "Cybersecurity", color: AMBER, size: 0.95 },
  { label: "Pentesting", color: AMBER, size: 0.85 },
  { label: "Payment Gateways", color: GREEN, size: 0.9 },
  { label: "NBFC Systems", color: MINT, size: 0.8 },
  { label: "Raspberry Pi", color: SKY, size: 0.85 },
  { label: "Embedded", color: SKY, size: 0.85 },
];

function Word({ position, label, color, size, onCursor }) {
  const ref = useRef();
  const [hovered, setHovered] = useState(false);

  const over = useCallback(
    (e) => {
      e.stopPropagation();
      setHovered(true);
      onCursor(true);
    },
    [onCursor]
  );
  const out = useCallback(() => {
    setHovered(false);
    onCursor(false);
  }, [onCursor]);

  // Smoothly ease the scale on hover.
  useFrame(() => {
    if (!ref.current) return;
    const target = hovered ? 1.35 : 1;
    ref.current.scale.x += (target - ref.current.scale.x) * 0.18;
    ref.current.scale.y = ref.current.scale.z = ref.current.scale.x;
  });

  return (
    <Billboard position={position}>
      <Text
        ref={ref}
        fontSize={size}
        color={hovered ? "#ffffff" : color}
        anchorX="center"
        anchorY="middle"
        onPointerOver={over}
        onPointerOut={out}
        outlineWidth={hovered ? 0.02 : 0}
        outlineColor={color}
        font="https://fonts.gstatic.com/s/orbitron/v25/yMJMMIlzdpvBhQQL_SC3X9yhF25-T1nyGy6BoWg1.woff"
      >
        {label}
      </Text>
    </Billboard>
  );
}

function Cloud({ radius = 9, onCursor }) {
  const group = useRef();

  const items = useMemo(() => {
    const count = SKILLS.length;
    const golden = Math.PI * (1 + Math.sqrt(5));
    return SKILLS.map((skill, i) => {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / count);
      const theta = golden * i;
      const x = Math.sin(phi) * Math.cos(theta) * radius;
      const y = Math.sin(phi) * Math.sin(theta) * radius;
      const z = Math.cos(phi) * radius;
      return { ...skill, position: [x, y, z] };
    });
  }, [radius]);

  useFrame((state, delta) => {
    if (!group.current) return;
    group.current.rotation.y += delta * 0.12;
    // gentle parallax tilt that follows the pointer
    const tx = state.pointer.y * 0.25;
    group.current.rotation.x += (tx - group.current.rotation.x) * 0.04;
  });

  return (
    <group ref={group}>
      {items.map((item) => (
        <Word key={item.label} {...item} onCursor={onCursor} />
      ))}
    </group>
  );
}

export default function SkillSphere() {
  const setCursor = useCallback((active) => {
    document.body.style.cursor = active ? "pointer" : "auto";
  }, []);

  return (
    <Canvas
      camera={{ position: [0, 0, 26], fov: 50 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      onCreated={({ gl }) => gl.setClearColor(0x000000, 0)}
    >
      <fog attach="fog" args={["#0b0d14", 22, 42]} />
      <Stars
        radius={60}
        depth={40}
        count={1200}
        factor={3}
        saturation={0}
        fade
        speed={0.6}
      />
      <Suspense fallback={null}>
        <Cloud onCursor={setCursor} />
      </Suspense>
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        rotateSpeed={0.5}
        autoRotate={false}
      />
    </Canvas>
  );
}
